// src/pages/Accounting/Reconcile/History/RowActionMenu.tsx

import type React from "react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Download, FileText, Printer, Undo2 } from "lucide-react";
import { PALETTE } from "../constants";

interface RowActionMenuProps {
  entryId: string;
  anchor: HTMLButtonElement;
  canUndo: boolean;
  onViewReport: (entryId: string) => void;
  onPrintReport: (entryId: string) => void;
  onExportPdf: (entryId: string) => void;
  onUndoReconciliation: (entryId: string) => void;
  onClose: () => void;
}

const POP_WIDTH = 220;

export function RowActionMenu({
  entryId,
  anchor,
  canUndo,
  onViewReport,
  onPrintReport,
  onExportPdf,
  onUndoReconciliation,
  onClose,
}: RowActionMenuProps) {
  const [coords, setCoords] = useState<{ top: number; right: number } | null>(null);
  const popRef = useRef<HTMLDivElement>(null);

  // Compute right-aligned position from the anchor
  useEffect(() => {
    const compute = () => {
      const rect = anchor.getBoundingClientRect();
      const right = Math.max(16, window.innerWidth - rect.right);
      setCoords({ top: rect.bottom + 6, right });
    };
    compute();
    window.addEventListener("resize", compute);
    window.addEventListener("scroll", compute, true);
    return () => {
      window.removeEventListener("resize", compute);
      window.removeEventListener("scroll", compute, true);
    };
  }, [anchor]);

  // Click outside closes
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (popRef.current?.contains(target)) return;
      if (anchor.contains(target)) return;
      onClose();
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [onClose, anchor]);

  if (!coords) return null;

  const popStyle: React.CSSProperties = {
    position: "fixed",
    top: coords.top,
    right: coords.right,
    background: "#fff",
    border: `1px solid ${PALETTE.line}`,
    borderRadius: 10,
    boxShadow: "0 10px 32px rgba(0, 0, 0, 0.14)",
    padding: "6px 0",
    width: POP_WIDTH,
    zIndex: 1000,
  };

  const handle = (fn: (id: string) => void) => () => {
    fn(entryId);
    onClose();
  };

  return createPortal(
    <div ref={popRef} style={popStyle}>
      <MenuItem icon={<FileText size={14} />} label="View report" onClick={handle(onViewReport)} />
      <MenuItem icon={<Printer size={14} />} label="Print report" onClick={handle(onPrintReport)} />
      <MenuItem icon={<Download size={14} />} label="Export as PDF" onClick={handle(onExportPdf)} />
      <div style={dividerStyle} />
      <MenuItem
        icon={<Undo2 size={14} />}
        label="Undo reconciliation"
        onClick={handle(onUndoReconciliation)}
        disabled={!canUndo}
        danger
      />
    </div>,
    document.body,
  );
}

interface MenuItemProps {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
}

function MenuItem({ icon, label, onClick, disabled, danger }: MenuItemProps) {
  const color = disabled ? PALETTE.ink4 : danger ? "#B42318" : PALETTE.ink;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        ...itemStyle,
        color,
        cursor: disabled ? "not-allowed" : "pointer",
      }}
    >
      <span style={{ display: "inline-flex", color }}>{icon}</span>
      {label}
      {disabled && <span style={adminHintStyle}>Admins only</span>}
    </button>
  );
}

const itemStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  width: "100%",
  background: "transparent",
  border: 0,
  textAlign: "left",
  padding: "9px 14px",
  fontSize: 13,
  fontWeight: 500,
  fontFamily: "inherit",
};
const adminHintStyle: React.CSSProperties = {
  marginLeft: "auto",
  fontSize: 10.5,
  color: PALETTE.ink4,
  fontWeight: 500,
};
const dividerStyle: React.CSSProperties = {
  height: 1,
  background: PALETTE.lineSoft,
  margin: "4px 0",
};